export const curriculumData = {
  '1-2': {
    title: 'Phase 1: Foundations',
    subtitle: 'Ages 1-2',
    icon: '/assets/P1F.webp',
    color: 'from-pink-400 to-rose-500',
    theme: 'Sounds and Senses',
    description: 'Building sensory awareness, early language, and object recognition through play and imitation',
    focus: ['Sound awareness and listening', 'Object recognition and labeling', 'Early motor control for pre-writing', 'Rhythm, rhyme, and simple imitation', 'Book awareness (turning pages, pointing, naming)'],
    parentRole: 'Guide exposure and encourage curiosity. No testing — only play and conversation.',
    lessonStructure: {
      weekly: '1 Week = 5 Activities',
      daily: '10-20 minutes per day',
      quarterly: 'Deep mastery of 2-3 developmental domains'
    },
    quarters: [
      {
        name: 'Quarter 1',
        weeks: '1-13',
        theme: 'Sounds & Senses',
        color: 'from-pink-300 to-rose-400',
        description: 'Sound play — animal noises, rhythm claps, name sounds',
        weekBreakdown: [
          { weeks: '1-3', focus: 'Sound play — animal noises, rhythm claps, name sounds' },
          { weeks: '4', focus: 'Integrate via music and echo games' },
          { weeks: '5-7', focus: 'Object naming and matching' },
          { weeks: '8', focus: 'Revisit early sounds in daily routines' },
          { weeks: '9-12', focus: 'Rhyming exposure, storytime sound play' },
          { weeks: '13', focus: 'Quarter review — "Our Favorite Sounds"' }
        ],
        skillRecurrence: 'Week 1\'s sound games reappear in Week 8 with new environmental sounds',
        activities: ['Animal sound games', 'Rhythm clapping', 'Name recognition songs', 'Object naming routines'],
        enrichment: ['Sound hunts', 'Sensory bins', 'Hand clap rhythms', 'Peek-a-boo labeling']
      },
      {
        name: 'Quarter 2',
        weeks: '14-26',
        theme: 'Words in Our World',
        color: 'from-orange-300 to-amber-400',
        description: 'Labeling, naming, pointing',
        weekBreakdown: [
          { weeks: '14-17', focus: 'Daily object labeling and repetition' },
          { weeks: '18-21', focus: 'Action words through play' },
          { weeks: '22-25', focus: 'Building 2-word phrases' },
          { weeks: '26', focus: 'Quarter review through familiar books' }
        ],
        skillRecurrence: 'Reintroduce skills every 3 weeks via books, songs, routines',
        activities: ['Point and name games', 'Picture book exploration', 'Routine narration', 'Simple command following'],
        enrichment: ['Mealtime narration', 'Walk and label games', 'Family photo naming', 'Toy sorting by name']
      },
      {
        name: 'Quarter 3',
        weeks: '27-39',
        theme: 'Shapes, Colors & Touch',
        color: 'from-yellow-300 to-orange-400',
        description: 'Visual literacy: shapes, colors. Tactile exploration',
        weekBreakdown: [
          { weeks: '27-30', focus: 'Shape recognition and naming' },
          { weeks: '31-34', focus: 'Color identification in environment' },
          { weeks: '35-38', focus: 'Texture exploration (sand, water, fabric)' },
          { weeks: '39', focus: 'Quarter review — sensory celebration' }
        ],
        skillRecurrence: 'Integrate tactile learning with previously learned vocabulary',
        activities: ['Shape sorting', 'Color hunts', 'Sensory bins', 'Texture matching'],
        enrichment: ['Environmental color hunt', 'Shape crafts', 'Water/sand play', 'Fabric texture books']
      },
      {
        name: 'Quarter 4',
        weeks: '40-52',
        theme: 'Storytime Beginnings',
        color: 'from-green-300 to-teal-400',
        description: 'Parents model reading aloud. Children turn pages, predict outcomes',
        weekBreakdown: [
          { weeks: '40-43', focus: 'Page turning and book handling' },
          { weeks: '44-47', focus: 'Picture predictions and pointing' },
          { weeks: '48-51', focus: 'Simple story sequences' },
          { weeks: '52', focus: 'Year-end review and milestone celebration' }
        ],
        activities: ['Read-aloud sessions', 'Picture pointing', 'Story actions', 'Book choosing'],
        enrichment: ['Story retelling with props', 'Make predictions', 'Act out stories', 'Create story endings']
      }
    ],
    outcomes: [
      'Recognizes 50-100 common objects',
      'Responds to simple commands',
      'Shows interest in books',
      'Attempts to imitate sounds',
      'Enjoys rhythmic activities'
    ]
  },
  '2-3': {
    title: 'Phase 2: Explorers',
    subtitle: 'Ages 2-3',
    icon: '/assets/P2F.webp',
    color: 'from-green-400 to-teal-500',
    theme: 'My World in Words',
    description: 'Expanding vocabulary, building listening comprehension, and introducing print awareness',
    focus: ['Vocabulary growth (naming objects and actions)', 'Phonemic awareness (recognizing beginning sounds)', 'Print awareness (books, letters, and names)', 'Listening comprehension (following short stories)', 'Fine motor skills (scribbling, pointing, tracing)'],
    parentRole: 'Narrate daily life, model language, make repetition playful and engaging. Use daily activities to reinforce words.',
    quarters: [
      {
        name: 'Quarter 1',
        weeks: '1-13',
        theme: 'Object Naming & Repetition',
        color: 'from-green-300 to-emerald-400',
        description: 'Daily life objects, family members, simple verbs',
        weekBreakdown: [
          { weeks: '1-2', focus: 'Object naming and repetition ("cup," "dog," "car")' },
          { weeks: '3', focus: 'Listening to rhythm and sound in speech' },
          { weeks: '4', focus: 'Review Week — sound and naming games' },
          { weeks: '5-6', focus: 'Identifying family members and people' },
          { weeks: '7-8', focus: 'Everyday actions (run, eat, sleep)' },
          { weeks: '9-12', focus: 'Revisit naming skills in stories' },
          { weeks: '13', focus: 'Quarter Review — "Our Word Parade!"' }
        ],
        skillRecurrence: 'Week 1\'s "naming household objects" returns in Week 9 during storytime',
        activities: ['Daily naming routines', 'Family photo games', 'Action charades', 'Story object hunts'],
        parentGuidance: 'Name items during routines; point and repeat daily. Use photos to practice names.'
      },
      {
        name: 'Quarter 2',
        weeks: '14-26',
        theme: 'Sounds Turn Into Letters',
        color: 'from-teal-300 to-cyan-400',
        description: 'Letter sounds and first-name recognition',
        weekBreakdown: [
          { weeks: '14-15', focus: 'Listening for beginning sounds ("B is for ball")' },
          { weeks: '16', focus: 'Drawing and tracing large letters' },
          { weeks: '17', focus: 'Review — letter sound games' },
          { weeks: '18-20', focus: 'First-name recognition' },
          { weeks: '21-23', focus: 'Introduce alphabet groups (A–F, G–L, etc.)' },
          { weeks: '24-25', focus: 'Story sound hunt' },
          { weeks: '26', focus: 'Quarter Review — "Alphabet Dance Party!"' }
        ],
        skillRecurrence: 'Letter sounds (Week 14) appear again in Week 24\'s "sound hunt"',
        activities: ['Beginning sound games', 'Letter tracing', 'Name recognition', 'Alphabet songs'],
        parentGuidance: 'Use toys and name the first sound aloud. Help child find their name on stickers.'
      },
      {
        name: 'Quarter 3',
        weeks: '27-39',
        theme: 'Talk, Move & Create',
        color: 'from-blue-300 to-indigo-400',
        description: 'Word combinations, rhymes, sequencing actions',
        weekBreakdown: [
          { weeks: '27-28', focus: 'Describe actions and feelings' },
          { weeks: '29', focus: 'Rhyming fun (cat, hat, bat)' },
          { weeks: '30', focus: 'Review — rhymes and actions' },
          { weeks: '31-33', focus: 'Word combinations ("big car," "red ball")' },
          { weeks: '34-36', focus: 'Matching pictures and words' },
          { weeks: '37-38', focus: 'Story sequencing (first, next, last)' },
          { weeks: '39', focus: 'Quarter Review — "We Tell Stories!"' }
        ],
        skillRecurrence: 'Rhyming from Week 29 reappears in Week 34 as sound–word matching',
        activities: ['Feeling charades', 'Rhyme time', 'Picture-word matching', 'Story retelling'],
        parentGuidance: 'Model short phrases during play. Use 3-step picture stories for sequencing.'
      },
      {
        name: 'Quarter 4',
        weeks: '40-52',
        theme: 'Little Readers, Big Thinkers',
        color: 'from-purple-300 to-pink-400',
        description: 'Story comprehension, print awareness, early writing',
        weekBreakdown: [
          { weeks: '40-41', focus: 'Choosing favorite books' },
          { weeks: '42-44', focus: 'Understanding sequence and cause-effect' },
          { weeks: '45', focus: 'Review — "Storytime Replay!"' },
          { weeks: '46-48', focus: 'Recognize print in environment (signs, labels)' },
          { weeks: '49-51', focus: 'Story retelling with art' },
          { weeks: '52', focus: 'Year Review — "My Reading Scrapbook!"' }
        ],
        skillRecurrence: 'Story sequencing (Week 37) revisits in Week 42 as cause-and-effect play',
        activities: ['Book selection', 'What happens next?', 'Environmental print hunt', 'Story art'],
        parentGuidance: 'Let child pick books nightly. Point out words on everyday items.'
      }
    ],
    outcomes: [
      'Recognize and name 100-200 common objects',
      'Identify 6-10 letters and their sounds',
      'Follow short stories with simple sequencing',
      'Begin scribbling or marking intentionally',
      'Show preference for familiar books and sounds'
    ]
  },
  '3-4': {
    title: 'Phase 3: Thinkers',
    subtitle: 'Ages 3-4',
    icon: '/assets/P3F.webp',
    color: 'from-blue-400 to-indigo-500',
    theme: 'Letters Come Alive',
    description: 'Transitioning from oral language to pre-reading through print, rhyme, and storytelling',
    focus: ['Phonemic awareness (blending sounds)', 'Alphabet mastery (upper- and lowercase letters)', 'Vocabulary expansion (categories, opposites, adjectives)', 'Listening comprehension and retelling', 'Fine motor refinement for writing'],
    parentRole: 'Point, sing, trace letters, model sound-letter connection. Model reading aloud daily, praise progress.',
    quarters: [
      {
        name: 'Quarter 1',
        weeks: '1-13',
        theme: 'Alphabet Exploration',
        color: 'from-blue-300 to-sky-400',
        description: 'Introduce letters, sounds, and patterns',
        weekBreakdown: [
          { weeks: '1-2', focus: 'Alphabet exploration (A–F)' },
          { weeks: '3', focus: 'Phoneme focus ("a" in apple, "b" in ball)' },
          { weeks: '4', focus: 'Review Week — Alphabet Sound Hunt' },
          { weeks: '5-7', focus: 'Alphabet (G–L)' },
          { weeks: '8', focus: 'Skill Recurrence: review A–F sounds' },
          { weeks: '9-12', focus: 'Alphabet (M–Z)' },
          { weeks: '13', focus: 'Quarter Review — "Alphabet Fair!"' }
        ],
        skillRecurrence: 'Sound matching from Week 3 returns in Week 8 with motor tracing',
        activities: ['Letter introduction songs', 'Sound-motion games', 'Alphabet hunts', 'Letter crafts'],
        parentGuidance: 'Introduce 1-2 letters daily with visuals. Use songs and movement for each sound.'
      },
      {
        name: 'Quarter 2',
        weeks: '14-26',
        theme: 'Rhymes & Sequences',
        color: 'from-indigo-300 to-purple-400',
        description: 'Rhyming games, cause-effect sequences',
        weekBreakdown: [
          { weeks: '14-15', focus: 'Beginning sound isolation' },
          { weeks: '16-17', focus: 'Ending sound identification' },
          { weeks: '18', focus: 'Review — rhyming and matching' },
          { weeks: '19-21', focus: 'Blending two sounds (c + a = ca)' },
          { weeks: '22-24', focus: 'Building CVC words (cat, hat, mat)' },
          { weeks: '25', focus: 'Skill Recurrence — return to early blends' },
          { weeks: '26', focus: 'Quarter Review — "Sound Builders"' }
        ],
        skillRecurrence: 'Blending introduced in Week 19 reappears in Week 25 with CVC completion',
        activities: ['Sound isolation games', 'Rhyme matching', 'Phoneme blending', 'Word building'],
        parentGuidance: 'Use blocks or cards for each sound. Blend aloud using toys or pictures.'
      },
      {
        name: 'Quarter 3',
        weeks: '27-39',
        theme: 'Story Builders',
        color: 'from-purple-300 to-fuchsia-400',
        description: 'Story sequencing, vocabulary expansion, character/emotion recognition',
        weekBreakdown: [
          { weeks: '27-29', focus: 'Story sequencing (beginning, middle, end)' },
          { weeks: '30', focus: 'Review — retell stories from memory' },
          { weeks: '31-33', focus: 'Character and emotion recognition' },
          { weeks: '34-35', focus: 'Vocabulary expansion — opposites and categories' },
          { weeks: '36', focus: 'Skill Recurrence — storytelling with new words' },
          { weeks: '37-38', focus: 'Predicting outcomes in stories' },
          { weeks: '39', focus: 'Quarter Review — "Our Story Show"' }
        ],
        skillRecurrence: 'Emotion vocabulary (Week 31) reappears in Week 36 within storytelling',
        activities: ['3-panel stories', 'Character emotions', 'Category sorting', 'Prediction games'],
        parentGuidance: 'Use 3-panel picture stories. Ask "How does the character feel?"'
      },
      {
        name: 'Quarter 4',
        weeks: '40-52',
        theme: 'Ready to Read',
        color: 'from-pink-300 to-rose-400',
        description: 'Print recognition, early blending, guided story creation',
        weekBreakdown: [
          { weeks: '40-41', focus: 'Review A–Z and key sounds' },
          { weeks: '42-44', focus: 'Simple sight words (the, is, it, at)' },
          { weeks: '45', focus: 'Review — early reader books' },
          { weeks: '46-48', focus: 'Writing name and simple words' },
          { weeks: '49-51', focus: 'Story creation ("My own book")' },
          { weeks: '52', focus: 'Year Review — "I\'m a Reader!"' }
        ],
        skillRecurrence: 'Sight words from Week 42 reused in Week 49 for child-authored books',
        activities: ['Alphabet review', 'Sight word games', 'Name writing', 'Story creation'],
        parentGuidance: 'Practice matching letter cards. Let child read with you or fill in words.'
      }
    ],
    outcomes: [
      'Recognize all 26 letters (upper and lowercase)',
      'Identify 15-20 common sight words',
      'Blend simple phonemes into full words',
      'Sequence and retell short stories',
      'Begin writing name and basic shapes',
      'Show excitement and confidence about reading'
    ]
  },
  '4-5': {
    title: 'Phase 4: Readers',
    subtitle: 'Ages 4-5',
    icon: '/assets/P4F.webp',
    color: 'from-indigo-400 to-purple-500',
    theme: 'Independent Readers & Writers',
    description: 'Developing early reading fluency, comprehension, and expression with phonics and sight words',
    focus: ['Phonics and decoding (CVC and blends)', 'Sight word recognition and fluency', 'Story comprehension and retelling', 'Sentence writing and early composition', 'Listening, reasoning, and prediction'],
    parentRole: 'Model decoding, point and read aloud. Encourage independent attempts, listen supportively, model curiosity about text.',
    quarters: [
      {
        name: 'Quarter 1',
        weeks: '1-13',
        theme: 'Sound + Sight = Reading',
        color: 'from-indigo-300 to-blue-400',
        description: 'CVC words, blending sounds',
        weekBreakdown: [
          { weeks: '1-2', focus: 'CVC word practice (cat, dog, sun)' },
          { weeks: '3', focus: 'Sound blending and segmenting games' },
          { weeks: '4', focus: 'Review — CVC and rhyme practice' },
          { weeks: '5-7', focus: 'Consonant blends (bl, st, tr, gr)' },
          { weeks: '8', focus: 'Skill Recurrence — CVC + new blends' },
          { weeks: '9-12', focus: 'Word families (-at, -og, -it)' },
          { weeks: '13', focus: 'Quarter Review — "Word Builder Party!"' }
        ],
        skillRecurrence: 'Blends from Week 5 revisited in Week 8 and Week 12 word-family work',
        activities: ['CVC word practice', 'Blending games', 'Word families', 'Word ladders'],
        parentGuidance: 'Practice words on paper and read aloud daily. Say words slowly for blending practice.'
      },
      {
        name: 'Quarter 2',
        weeks: '14-26',
        theme: 'Building Sentences',
        color: 'from-purple-300 to-violet-400',
        description: 'Sight words, short sentence formation',
        weekBreakdown: [
          { weeks: '14-15', focus: 'Sight word review and expansion' },
          { weeks: '16-18', focus: 'Building sentences ("The cat runs.")' },
          { weeks: '19', focus: 'Review — sentence games' },
          { weeks: '20-22', focus: 'Reading short predictable books' },
          { weeks: '23-25', focus: 'Introducing punctuation' },
          { weeks: '26', focus: 'Quarter Review — "I Can Read!"' }
        ],
        skillRecurrence: 'Sight words from Week 14 recur in Week 20 sentences, then Week 26 reading',
        activities: ['Sight word cards', 'Sentence building', 'Predictable books', 'Punctuation games'],
        parentGuidance: 'Rearrange cards to form sentences. Pause and ask questions while reading.'
      },
      {
        name: 'Quarter 3',
        weeks: '27-39',
        theme: 'Stories Have Meaning',
        color: 'from-fuchsia-300 to-pink-400',
        description: 'Story sequencing, inference, vocabulary growth',
        weekBreakdown: [
          { weeks: '27-28', focus: 'Story sequencing and recall' },
          { weeks: '29', focus: 'Review — act out stories' },
          { weeks: '30-32', focus: 'Making inferences ("Why did the dog run?")' },
          { weeks: '33-35', focus: 'Vocabulary growth — adjectives and opposites' },
          { weeks: '36', focus: 'Skill Recurrence — add adjectives to old stories' },
          { weeks: '37-38', focus: 'Emotions and empathy in stories' },
          { weeks: '39', focus: 'Quarter Review — "Our Story Theater!"' }
        ],
        skillRecurrence: 'Sequencing from Week 27 returns in Week 36 with vocabulary expansion',
        activities: ['Story recall', 'Story drama', 'Inference questions', 'Adjective games'],
        parentGuidance: 'Ask what happened first, next, last. Ask open-ended comprehension questions.'
      },
      {
        name: 'Quarter 4',
        weeks: '40-52',
        theme: 'Independent Readers and Writers',
        color: 'from-rose-300 to-red-400',
        description: 'Early fluency, writing, and comprehension',
        weekBreakdown: [
          { weeks: '40-41', focus: 'Sight word mastery (review all 50+)' },
          { weeks: '42-44', focus: 'Reading short paragraphs and stories' },
          { weeks: '45', focus: 'Review — story sequencing + inference' },
          { weeks: '46-48', focus: 'Writing practice (sentences and short stories)' },
          { weeks: '49-51', focus: '"All About Me" project' },
          { weeks: '52', focus: 'Year Review — "I\'m a Reader Celebration!"' }
        ],
        skillRecurrence: 'Writing sentences (Week 16) reappears as full-story creation in Week 46',
        activities: ['Sight word mastery', 'Paragraph reading', 'Sentence writing', 'Story projects'],
        parentGuidance: 'Encourage tracking with finger. Provide lined paper for self-expression.'
      }
    ],
    outcomes: [
      'Read 50-75 sight words confidently',
      'Decode simple sentences and short stories',
      'Comprehend, recall, and predict story events',
      'Write basic sentences and short narratives',
      'Show emotional connection while reading aloud',
      'Transition smoothly to kindergarten-level literacy'
    ]
  }
};
